import React, { createContext, useState, useEffect } from 'react';
import { User, AuthError, Session } from '@supabase/supabase-js';
import { supabase, testSupabaseConnection, clearAuthCache } from '../lib/supabase';
import { products } from '../stripe-config';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ user: User; session: Session }>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string, name?: string, planId?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signIn: async () => { throw new Error('Not implemented'); },
  signInWithGoogle: async () => {},
  signUp: async () => {},
  signOut: async () => {},
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial auth session loaded:', session ? 'active' : 'none');
      
      // If no session, clear any stale auth data
      if (!session) {
        clearAuthCache();
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {      
      console.log('Auth state changed:', _event, 'User ID:', session?.user?.id);
      
      // If auth error or sign out, clear auth cache
      if (_event === 'SIGNED_OUT' || _event === 'TOKEN_REFRESHED' || _event === 'USER_UPDATED') {
        console.log('Auth event requires cache clearing:', _event);
        clearAuthCache();
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log(`Signing in with email: ${email}`);
      
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('Sign in error from Supabase:', error);
        throw error;
      }
      
      // Explicit validation of auth response data
      if (!data) {
        console.error('Sign in failed: No data returned from auth service');
        throw new Error('Authentication failed: No response data');
      }
      
      if (!data.user) {
        console.error('Sign in failed: No user object in response data', data);
        throw new Error('Authentication failed: User information missing');
      }
      
      if (!data.session) {
        console.error('Sign in failed: No session object in response data', data);
        throw new Error('Authentication failed: Session information missing');
      }
      
      console.log('Sign in successful, user:', data.user?.id);
      
      // Update context state immediately
      setSession(data.session);
      setUser(data.user);
      
      return { user: data.user, session: data.session };
    } catch (error) {
      const authError = error as AuthError;
      console.error('Sign in error:', authError);
      throw authError;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) throw error;
    } catch (error) {
      const authError = error as AuthError;
      console.error('Google sign in error:', authError);
      throw authError;
    }
  };

  const signUp = async (email: string, password: string, name?: string, planId?: string) => {
    try {
      // Test database connection before proceeding
      let isConnected = false;
      try {
        isConnected = await testSupabaseConnection();
      } catch (connErr) {
        console.error('Connection test failed:', connErr);
        // Continue anyway, as this might be a temporary issue
      }

      // Default to FREE plan if none specified
      const freePlanId = products.free.priceId;
      const selectedPlanId = planId || freePlanId;
      
      console.log(`Signing up user with email: ${email}, plan: ${selectedPlanId} (${selectedPlanId === freePlanId ? 'FREE' : 'PAID'})`);
      
      // Sign up the user with Supabase Auth
      const { error: signUpError, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name || '',
            selected_plan: selectedPlanId, // Store intended plan in metadata
            onboarding_completed: false
          },
          emailRedirectTo: `${window.location.origin}/dashboard`
        },
      });
      
      if (signUpError) {
        console.error('Auth signup error:', signUpError);
        throw signUpError;
      }
      
      if (!data.user) {
        console.error('User creation failed - no user data returned');
        throw new Error('User creation failed');
      }
      
      console.log(`User signed up successfully with ID: ${data.user.id}, redirecting to onboarding`);
      
      // Manually ensure profile is created
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            full_name: name || '',
            onboarding_completed: false,
            plan_id: selectedPlanId
          });
          
        if (profileError) {
          console.warn('Error creating profile:', profileError);
        }
      } catch (profileErr) {
        console.warn('Error creating profile:', profileErr);
      }
      
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      console.log('Signing out user');

      // Clear auth cache before signing out
      clearAuthCache();
      
      // First clear the local state
      setUser(null);
      setSession(null);
      
      // Then attempt to sign out from Supabase
      await supabase.auth.signOut();
      
      console.log('Sign out successful');
    } catch (error) {
      console.error('Sign out error:', error);
      // Don't throw the error to prevent blocking UI
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user,
        session,
        loading, 
        signIn,
        signInWithGoogle, 
        signUp, 
        signOut 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};